import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Pins the AgenticRx panel — tabs, conversation, input bar — to the window, so the
 * conversation list scrolls inside the panel while the shell can still scroll to
 * reveal the full left/right sidebars.
 *
 * Why this is needed: the desktop app shell scrolls. `.level1` is a stretch row whose
 * height comes from its tallest child — the left patient sidebar (allergies, active
 * drugs, PDMP, Rx menu) — and `.level2` → `.level3` → `.level4` all inherit that height
 * (see Theme/index.tsx). The white card this panel lives in is therefore routinely
 * taller than the window, so anything laid out inside it scrolls away with the sidebar.
 * Mobile sidesteps this via `[breakpoints.down("md")]: { flex: 1, overflow: "hidden" }`
 * on those same shell classes; desktop never got an equivalent.
 *
 * `position: fixed` rather than `sticky`: sticky keeps the panel in flow inside that
 * over-tall card, which leaves its behaviour dependent on the card's height, on which
 * ancestor happens to be the scrollport, and on there being travel left in the
 * containing block. Fixed depends on none of that, and the input bar in this same tree
 * has always used it successfully.
 *
 * The geometry comes from a zero-height sentinel left behind in normal flow, which is
 * what makes this stable: the panel is out of flow once fixed, so it cannot influence
 * the sentinel, and re-measuring can never feed the panel's own position back into its
 * offset. The sentinel supplies the content column's left/width (so the panel tracks
 * sidebar collapse). `top` is derived from the sentinel's document offset minus the
 * current scroll so the panel tracks under the header, then pins to the viewport top
 * once the header has scrolled away — without locking document overflow.
 */
export function useViewportLock() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const restingTopRef = useRef(0);
  const [box, setBox] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const measure = useCallback(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const card = el.closest(".level4");
    const cardRect = card?.getBoundingClientRect();

    const restingTop = Math.round(r.top + window.scrollY);
    restingTopRef.current = restingTop;

    let top = Math.max(0, restingTop - window.scrollY);
    let left = Math.round(r.left);
    let width = Math.round(r.width);
    let bottom = window.innerHeight;

    if (cardRect) {
      const cardLeft = Math.round(cardRect.left);
      const cardRight = Math.round(cardRect.right);
      const cardTop = Math.round(cardRect.top);
      const cardBottom = Math.round(cardRect.bottom);
      top = Math.max(top, Math.max(0, cardTop));
      left = Math.max(left, cardLeft);
      width = Math.max(0, Math.min(Math.round(r.right), cardRight) - left);
      bottom = Math.min(bottom, cardBottom);
    }

    const height = Math.max(0, bottom - top);
    const next = { top, left, width, height };
    setBox((prev) =>
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    // Keep left/right sidebars scrollable; update the fixed panel as the shell moves.
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [measure]);

  // Column width changes (sidebar collapse) and header/ribbon reflows.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const card = el.closest(".level4");
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    if (card) ro.observe(card);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [measure]);

  const lockStyle: CSSProperties | null =
    box === null || box.width === 0 || box.height === 0
      ? null
      : {
          position: "fixed",
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: 2,
        };

  return { sentinelRef, lockStyle };
}
